import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import Chart from "react-apexcharts";
import { fetchTestTypeAnalysis, fetchAllProjectWiseHours } from "../../../API_Service/Dashboard_Slice";
import { companyID, Dropdown, DashboardCard, EmptyState, ChartSkeleton, InlineAlert } from "../../../Common/UIComponents";
import { useDashboardSignalR } from "../../../Common/useDashboardSignalR";
import {
  HOURS_COLORS,
  CHART_PALETTE,
  axisLabelStyle,
  axisTitleStyle,
  gridBase,
  tooltipStyleBase,
  tooltipBoxStyle,
  dataLabelStyle,
  chartChrome,
} from "../../../Common/chartTheme";

const CHART_DISPLAY_LIMIT = 10;

const getBarHeightPercent = (length) => {
  if (length === 1) return "40%";
  if (length === 2) return "50%";
  return "65%";
};

const buildChartOptions = (categories, calculatedHeight, barHeightPercent) => ({
  chart: {
    ...chartChrome,
    type: "bar",
    stacked: true,
    toolbar: {
      show: true,
      tools: {
        download: true,
        selection: true,
        zoom: true,
        zoomin: true,
        zoomout: true,
        pan: true,
        reset: true,
      },
    },
    height: calculatedHeight,
    animations: {
      enabled: true,
      speed: 800,
      animateGradually: {
        enabled: true,
        delay: 150,
      },
      dynamicAnimation: {
        enabled: true,
        speed: 150,
      },
    },
  },
  plotOptions: {
    bar: {
      horizontal: true,
      distributed: false,
      dataLabels: {
        position: "top",
      },
      barHeight: barHeightPercent,
      borderRadius: 6,
      borderRadiusApplication: "end",
    },
  },
  dataLabels: {
    enabled: true,
    formatter: (value) => {
      return value ? value.toFixed(2) : "0.00";
    },
    style: dataLabelStyle,
    background: {
      enabled: false,
    },
    offsetX: -5,
    textAnchor: "end",
  },
  stroke: {
    show: true,
    width: 0,
    colors: ["transparent"],
  },
  xaxis: {
    categories,
    type: "category",
    labels: {
      style: axisLabelStyle,
      offsetY: 5,
    },
    axisBorder: {
      show: true,
      color: "#e2e8f0",
      height: 1,
    },
    axisTicks: {
      show: false,
    },
    title: {
      text: "Hours",
      style: axisTitleStyle,
      offsetY: 10,
    },
  },
  yaxis: {
    type: "numeric",
    axisBorder: {
      show: true,
      color: "#e2e8f0",
      width: 1,
    },
    axisTicks: {
      show: false,
    },
    labels: {
      style: axisLabelStyle,
      offsetX: -5,
    },
    title: {
      text: "Test Types",
      style: axisTitleStyle,
    },
  },
  fill: {
    opacity: 1,
    type: "solid",
  },
  grid: {
    ...gridBase,
    xaxis: {
      lines: {
        show: true,
        opacity: 0.1,
      },
    },
    yaxis: {
      lines: {
        show: false,
      },
    },
    padding: {
      left: 0,
      right: 10,
      top: 0,
      bottom: 0,
    },
  },
  states: {
    hover: {
      filter: {
        type: "darken",
        value: 0.15,
      },
    },
    active: {
      filter: {
        type: "darken",
        value: 0.15,
      },
    },
  },
  colors: HOURS_COLORS,
  tooltip: {
    enabled: true,
    theme: "light",
    style: { ...tooltipStyleBase, ...tooltipBoxStyle },
    onDatasetHover: {
      highlightDataSeries: true,
    },
    shared: true,
    intersect: false,
    fillSeriesColor: false,
    y: {
      formatter: (value) => {
        return value ? value.toFixed(2) + " hrs" : "0 hrs";
      },
      title: {
        formatter: (seriesName) => seriesName + ": ",
      },
    },
    marker: {
      show: true,
    },
  },
  responsive: [
    {
      breakpoint: 1024,
      options: {
        plotOptions: {
          bar: {
            barHeight: "60%",
          },
        },
      },
    },
  ],
});

// The main tile shows this as a donut of total hours per test type (a
// different shape than the bar charts either side of it in the PM row —
// ProjectWiseHours and LabWiseProject); the billable/non-billable split
// that used to be two bars here is still available, just moved into the
// tooltip via `displayData`. The "View All" modal keeps the bar chart
// (via buildChartOptions above) since a long scannable list of many test
// types works better as bars than as a many-slice donut.
const buildDonutOptions = (names, displayData) => {
  const colors = names.map((_, i) => CHART_PALETTE[i % CHART_PALETTE.length]);
  return {
    chart: {
      ...chartChrome,
      type: "donut",
      animations: { enabled: true, speed: 400 },
    },
    labels: names,
    colors,
    legend: {
      position: "bottom",
      fontSize: "12px",
      labels: { colors: "#475569" },
      itemMargin: { horizontal: 8, vertical: 4 },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "62%",
          labels: {
            show: true,
            name: { show: true, fontSize: "13px", fontWeight: 600, color: "#475569" },
            value: {
              show: true,
              fontSize: "20px",
              fontWeight: 700,
              color: "#1e293b",
              formatter: (v) => `${parseFloat(v).toFixed(0)}h`,
            },
            total: {
              show: true,
              label: "Total Hours",
              fontSize: "13px",
              fontWeight: 600,
              color: "#475569",
              formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0).toFixed(0),
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
      style: { ...dataLabelStyle, colors: ["#ffffff"] },
    },
    stroke: { width: 2, colors: ["#ffffff"] },
    states: {
      hover: { filter: { type: "darken", value: 0.15 } },
      active: { filter: { type: "darken", value: 0.15 } },
    },
    tooltip: {
      enabled: true,
      theme: "light",
      style: { ...tooltipStyleBase, ...tooltipBoxStyle },
      // Default pie/donut tooltip positioning anchors to a point computed
      // along the hovered slice, which can land inside the donut hole and
      // collide with the center total/value label rendered there.
      // followCursor moves it to the mouse instead, but the mouse itself
      // is very often over that same center area on a chart this size —
      // same collision, different trigger. Pinning it to a fixed corner
      // sidesteps the problem entirely: it can never overlap the center
      // label no matter where the hovered slice or cursor is.
      fixed: {
        enabled: true,
        position: "topRight",
        offsetX: -8,
        offsetY: 8,
      },
      custom: ({ seriesIndex, w }) => {
        const row = displayData[seriesIndex] || {};
        const billable = (parseFloat(row.BillableHours) || 0).toFixed(2);
        const nonBillable = (parseFloat(row.NonBillableHours) || 0).toFixed(2);
        const total = (parseFloat(row.TotalHours) || 0).toFixed(2);
        const name = w.globals.labels[seriesIndex];
        const color = colors[seriesIndex % colors.length];
        return `
          <div style="padding:10px 14px;min-width:170px;background:#ffffff;border-radius:8px;border:1px solid #e2e8f0;box-shadow:0 8px 20px rgba(15,23,42,0.16);">
            <div style="display:flex;align-items:center;gap:6px;font-weight:700;font-size:13px;color:#1e293b;margin-bottom:6px;">
              <span style="width:9px;height:9px;border-radius:9999px;background:${color};display:inline-block;"></span>
              ${name}
            </div>
            <div style="font-size:12px;color:#475569;">Total: <b>${total} hrs</b></div>
            <div style="font-size:12px;color:${HOURS_COLORS[0]};">Billable: <b>${billable} hrs</b></div>
            <div style="font-size:12px;color:#64748b;">Non-Billable: <b>${nonBillable} hrs</b></div>
          </div>`;
      },
    },
  };
};

const TestTypeAnalysis = ({ selectedProjectID = 0, selectedProjectName = "", minHeight = 400, onClearFilter, onProjectSelected, projectListProp = [] }) => {
  const dispatch = useDispatch();
  const [series, setSeries] = useState([]);
  const [options, setOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEmpty, setIsEmpty] = useState(false);
  const [localSelectedProjectID, setLocalSelectedProjectID] = useState(0);
  const [displayProjectName, setDisplayProjectName] = useState("");
  const [projectList, setProjectList] = useState([]);
  const [chartHeight, setChartHeight] = useState(400);
  const [fullTestTypeData, setFullTestTypeData] = useState([]);
  const [showAllModal, setShowAllModal] = useState(false);
  const [allCompanyProjects, setAllCompanyProjects] = useState([]);

  const loadTestTypeAnalysis = useCallback((projectID = 0) => {
    if (!companyID) {
      setError("Company ID not found");
      setIsLoading(false);
      return;
    }

    console.log("📊 Loading Test Type Analysis - CompanyID:", companyID, "ProjectID:", projectID);
    setIsLoading(true);
    setError(null);
    setIsEmpty(false);

    // Clear existing data when loading new project
    setSeries([]);
    setOptions(null);

    dispatch(fetchTestTypeAnalysis({ companyID, projectID: projectID }))
      .then((result) => {
        console.log("🔍 Test Type Analysis Result:", result);
        console.log("🔍 Result Payload Type:", typeof result.payload, "Is Array:", Array.isArray(result.payload));

        if (!result || !result.payload) {
          console.error("❌ No payload in result");
          setError("Invalid response from server");
          setIsLoading(false);
          return;
        }

        let data = result.payload;

        if (result.payload && result.payload.data && Array.isArray(result.payload.data)) {
          data = result.payload.data;
          console.log("✅ Extracted data from payload.data");
        } else if (Array.isArray(result.payload)) {
          data = result.payload;
          console.log("✅ Using payload directly as array");
        } else {
          console.error("❌ Payload is not an array:", result.payload);
          setError("Invalid data format from server");
          setIsLoading(false);
          return;
        }

        // Filter out entries with 0 total hours
        const originalLength = data.length;
        data = data.filter((d) => d.TotalHours > 0);
        console.log(`📊 Filtered data: ${originalLength} -> ${data.length} (removed ${originalLength - data.length} zero-hour entries)`);

        if (!Array.isArray(data) || data.length === 0) {
          console.warn("⚠️ No test type data available after filtering");
          setIsEmpty(true);
          // Clear the chart when no data
          setSeries([]);
          setOptions(null);
          setIsLoading(false);
          return;
        }

        // Extract project list if this is all-projects view (projectID = 0)
        if (projectID === 0 && data.length > 0) {
          const uniqueProjects = [];
          const seenProjects = new Set();

          data.forEach((item) => {
            const projID = item.projectId || item.ProjectId || 0;
            const projName = item.projectName || item.ProjectName || "Unknown";

            if (projID && !seenProjects.has(projID)) {
              seenProjects.add(projID);
              uniqueProjects.push({ id: projID, name: projName });
            }
          });

          setProjectList(uniqueProjects);
          console.log("📋 Available Projects:", uniqueProjects);
        }

        // Keep the full dataset for the "View All" modal
        setFullTestTypeData(data);

        // Only show the top N entries in the main chart
        const displayData = data.slice(0, CHART_DISPLAY_LIMIT);

        const names = displayData.map((t) => t.TestTypeName || "Unknown");
        const totalHoursPerType = displayData.map(
          (t) => parseFloat(t.TotalHours) || (parseFloat(t.BillableHours) || 0) + (parseFloat(t.NonBillableHours) || 0),
        );

        console.log("📋 Test Types:", names);
        console.log("⏱️ Total Hours:", totalHoursPerType);

        setSeries(totalHoursPerType);

        // Donut has a fixed height, just kept at least as tall as the
        // bar chart next to it in the row for a tidy shared row height.
        const calculatedHeight = Math.max(360, minHeight);
        setChartHeight(calculatedHeight);

        setOptions(buildDonutOptions(names, displayData));

        setError(null);
      })
      .catch((err) => {
        console.error("❌ TestTypeAnalysis Error:", err);
        console.error("Error Message:", err?.message);
        console.error("Error Status:", err?.status);

        // Clear UI on error
        setSeries([]);
        setOptions(null);
        setProjectList([]);

        setError(err?.message || "Failed to load test type analysis");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [companyID, dispatch]);

  // Sync parent selectedProjectID with local state and find project name from projectList
  useEffect(() => {
    console.log("📊 Parent selectedProjectID changed to:", selectedProjectID);
    setLocalSelectedProjectID(selectedProjectID);

    if (selectedProjectID === 0) {
      setDisplayProjectName("");
      console.log("✅ Cleared project name (ID=0)");
    } else if (projectList && projectList.length > 0) {
      const project = projectList.find((p) => p.id === selectedProjectID);
      if (project) {
        setDisplayProjectName(project.name);
        console.log("✅ Found project name from list:", project.name);
      } else {
        console.warn("⚠️ Project ID", selectedProjectID, "not found in projectList of", projectList.length, "items");
      }
    } else {
      console.warn("⚠️ ProjectList is empty, cannot find project name");
    }
  }, [selectedProjectID, projectList]);

  // Sync minHeight changes - ensure chart height matches partner chart
  useEffect(() => {
    if (minHeight && chartHeight < minHeight) {
      console.log("📏 Syncing height to match partner chart. Current:", chartHeight, "Min:", minHeight);
      setChartHeight(minHeight);
    }
  }, [minHeight, chartHeight]);

  // Load test type analysis when local selection changes
  useEffect(() => {
    loadTestTypeAnalysis(localSelectedProjectID);
  }, [localSelectedProjectID, loadTestTypeAnalysis]);

  // Fetch the complete project list (not limited to top 10) for the dropdown
  useEffect(() => {
    if (!companyID) return;

    dispatch(fetchAllProjectWiseHours({ companyID }))
      .then((result) => {
        let data = result.payload;

        if (result.payload && result.payload.data && Array.isArray(result.payload.data)) {
          data = result.payload.data;
        } else if (Array.isArray(result.payload)) {
          data = result.payload;
        } else {
          data = [];
        }

        const mapped = data
          .map((p) => ({
            id: p.projectId || p.ProjectId || 0,
            name: p.projectName || p.ProjectName || "Unknown",
          }))
          .filter((p) => p.id);

        setAllCompanyProjects(mapped);
      })
      .catch((err) => {
        console.error("Failed to load full project list:", err);
      });
  }, [dispatch]);

  useDashboardSignalR(() => loadTestTypeAnalysis(localSelectedProjectID));

  // Build full (unlimited) chart series/options for the "View All" modal
  const allTestTypeNames = useMemo(
    () => fullTestTypeData.map((t) => t.TestTypeName || "Unknown"),
    [fullTestTypeData],
  );

  const allSeries = useMemo(
    () => [
      {
        name: "Billable Hours",
        data: fullTestTypeData.map((t) => parseFloat(t.BillableHours) || 0),
      },
      {
        name: "Non-Billable Hours",
        data: fullTestTypeData.map((t) => parseFloat(t.NonBillableHours) || 0),
      },
    ],
    [fullTestTypeData],
  );

  const allChartHeight = useMemo(
    () => Math.max(300, 35 * allTestTypeNames.length),
    [allTestTypeNames],
  );

  const allOptions = useMemo(() => {
    if (allTestTypeNames.length === 0) return null;
    return buildChartOptions(allTestTypeNames, allChartHeight, getBarHeightPercent(allTestTypeNames.length));
  }, [allTestTypeNames, allChartHeight]);

  // Prefer the full company project list (unlimited) over the internally
  // discovered list or the top-10 list handed down from the Project-wise
  // Hours chart, so every project shows up here.
  const projectSourceList =
    allCompanyProjects && allCompanyProjects.length > 0
      ? allCompanyProjects
      : projectList && projectList.length > 0
        ? projectList
        : projectListProp;

  const projectDropdownOptions = useMemo(
    () => [
      { label: "All Projects", value: 0 },
      ...(projectSourceList || []).map((p) => ({ label: p.name || "Unknown", value: p.id })),
    ],
    [projectSourceList],
  );

  const handleProjectDropdownChange = (projectId) => {
    const numericId = Number(projectId);
    const project = (projectSourceList || []).find((p) => p.id === numericId);
    setLocalSelectedProjectID(numericId);
    if (project) {
      setDisplayProjectName(project.name);
    }
    if (onProjectSelected && typeof onProjectSelected === "function") {
      onProjectSelected(numericId, project?.name || "");
    }
  };

  if (error) {
    return <InlineAlert type="error" message={`Error loading test type analysis: ${error}`} />;
  }

  if (isEmpty) {
    return (
      <DashboardCard icon="analytics" title="Test Type Analysis">
        <EmptyState message="No test type data available" />
      </DashboardCard>
    );
  }

  if (isLoading || !options) {
    return (
      <DashboardCard icon="analytics" title="Test Type Analysis">
        <ChartSkeleton height={256} />
      </DashboardCard>
    );
  }

  return (
    <>
    <DashboardCard
      icon="analytics"
      title="Test Type Analysis"
      subtitle="Track test type distribution and hours"
      actions={
        <>
          {fullTestTypeData.length > CHART_DISPLAY_LIMIT && (
            <button
              type="button"
              onClick={() => setShowAllModal(true)}
              className="px-3 py-1.5 text-xs font-semibold text-[#08757C] border border-[#08757C]/30 rounded-md hover:bg-[#08757C]/5 transition-colors"
            >
              View All
            </button>
          )}
          {/* Project Selector Dropdown */}
          <div className="w-48">
            <Dropdown
              options={projectDropdownOptions}
              value={localSelectedProjectID}
              onChange={handleProjectDropdownChange}
              placeholder="All Projects"
            />
          </div>

          {selectedProjectID > 0 && onClearFilter && (
            <button
              onClick={onClearFilter}
              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-md text-xs font-semibold transition-colors flex items-center gap-1.5"
            >
              <span>✕</span>
              <span>Clear</span>
            </button>
          )}
        </>
      }
    >
      {/* Donut of total hours per test type — hover a slice for its
          billable/non-billable split */}
      {series.length > 0 && options ? (
        <div className="chart-fade-in rounded-lg overflow-hidden bg-white flex justify-center">
          <Chart
            options={options}
            series={series}
            type="donut"
            height={chartHeight}
          />
        </div>
      ) : (
        <EmptyState message="No data available" />
      )}

      {/* Project Selector List - Only show when viewing all projects (localSelectedProjectID = 0) */}
      {localSelectedProjectID === 0 && projectList && projectList.length > 0 && (
        <div className="mt-6 pt-6 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 mb-3">📌 Click on a project to filter this analysis:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-40 overflow-y-auto">
            {projectList.map((project, idx) => (
              <button
                key={idx}
                onClick={() => {
                  console.log("✅ Selected Project from TestTypeAnalysis:", project.name, "ID:", project.id);
                  setLocalSelectedProjectID(project.id);
                  setDisplayProjectName(project.name);
                  if (onProjectSelected && typeof onProjectSelected === "function") {
                    onProjectSelected(project.id, project.name);
                  }
                }}
                className="p-2.5 rounded-md font-medium text-sm transition-all bg-slate-50 text-slate-700 hover:bg-[#08757C] hover:text-white border border-transparent hover:border-[#08757C]"
              >
                <span className="block truncate text-left">{project.name}</span>
                <span className="text-xs opacity-75 block text-left">ID: {project.id}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </DashboardCard>

    {/* View All Test Types Modal */}
    {showAllModal && (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => setShowAllModal(false)}
      >
        <div
          className="bg-white rounded-lg shadow-xl border border-slate-200/80 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">All Test Types</h3>
              <p className="text-xs text-slate-500 mt-0.5">Complete list of test types and hours</p>
            </div>
            <button
              type="button"
              onClick={() => setShowAllModal(false)}
              className="p-1.5 hover:bg-slate-100 rounded-md transition-colors"
            >
              <span className="material-symbols-outlined text-slate-500 text-[20px]">close</span>
            </button>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto p-4">
            {allSeries.length > 0 && allOptions ? (
              <>
                {/* Legend */}
                <div className="flex items-center gap-6 mb-4 pb-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: HOURS_COLORS[0] }}></div>
                    <span className="text-sm font-medium text-slate-600">Billable Hours</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: HOURS_COLORS[1] }}></div>
                    <span className="text-sm font-medium text-slate-600">Non-Billable Hours</span>
                  </div>
                  <div className="ml-auto text-sm text-slate-500">
                    Total Test Types: <span className="font-bold text-slate-900">{fullTestTypeData.length}</span>
                  </div>
                </div>

                <Chart
                  options={allOptions}
                  series={allSeries}
                  type="bar"
                  height={allChartHeight}
                />
              </>
            ) : (
              <EmptyState message="No data available" />
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default TestTypeAnalysis;
